<template>
  <!-- 图片列表容器 -->
  <div :class="[styles.imagePreviewList, listClass]">
    <div v-for="(url, index) in imageList" :key="index" :class="styles.imagePreviewItemWrapper">
      <img
        :src="addHttpPrefix(url)"
        :class="[styles.imagePreviewItem, itemClass]"
        draggable="false"
        @click="handleImageClick(index)"
        @error="handleImageError" />
      <!-- 删除按钮 -->
      <button
        v-if="enableDelete"
        :class="styles.imagePreviewDelete"
        @click.stop="handleDelete(index, url)">
        <SvgIcon name="svg/cs_img_delete.svg" :class="styles.imagePreviewDeleteIcon" />
      </button>
    </div>
  </div>

  <!-- 全屏预览 -->
  <Popup
    :contentClass="styles.popContent"
    v-model:visible="previewVisible"
    position="center"
    :maskClosable="true"
    :wrapperClass="styles.previewWrapper"
    @close="handleClosePreview">
    <div :class="styles.previewContainer" @click="handleClosePreview">
      <!-- 关闭按钮 -->
      <button :class="styles.previewClose" @click="handleClosePreview">
        <SvgIcon name="svg/close.svg" size="24" />
      </button>

      <!-- 图片 -->
      <img :src="addHttpPrefix(currentImageUrl)" :class="styles.previewImage" draggable="false" />

      <!-- 导航按钮 -->
      <button
        v-if="imageList.length > 1"
        :class="[styles.previewNav, styles.previewNavPrev]"
        @click.stop="handlePrev">
        <SvgIcon name="svg/arrow_left.svg" size="24" />
      </button>
      <button
        v-if="imageList.length > 1"
        :class="[styles.previewNav, styles.previewNavNext]"
        @click.stop="handleNext">
        <SvgIcon name="svg/arrow_right.svg" size="24" />
      </button>
    </div>
  </Popup>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import Popup from '@/components/base/popup/Popup.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import { addHttpPrefix } from '@/utils'

interface Props {
  src: string | string[]
  listClass?: string
  itemClass?: string
  enableDelete?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  listClass: '',
  itemClass: '',
  enableDelete: false
})

const emit = defineEmits<{
  delete: [index: number, url: string]
}>()

// 内部状态
const previewVisible = ref(false)
const currentIndex = ref(0)

// 处理 src 为数组
const imageList = computed<string[]>(() => {
  if (Array.isArray(props.src)) {
    return props.src
  }
  return [props.src]
})

// 当前预览的图片URL
const currentImageUrl = computed(() => {
  return imageList.value[currentIndex.value] || ''
})

// 点击图片打开预览
const handleImageClick = (index: number) => {
  currentIndex.value = index
  previewVisible.value = true
}

// 关闭预览
const handleClosePreview = () => {
  previewVisible.value = false
}

// 上一张
const handlePrev = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  } else {
    currentIndex.value = imageList.value.length - 1
  }
}

// 下一张
const handleNext = () => {
  if (currentIndex.value < imageList.value.length - 1) {
    currentIndex.value++
  } else {
    currentIndex.value = 0
  }
}

// 删除图片
const handleDelete = (index: number, url: string) => {
  emit('delete', index, url)
}

// 图片加载错误处理 TODO
const handleImageError = () => {
  console.warn('Image load error')
}

// 键盘事件处理
const handleKeydown = (e: KeyboardEvent) => {
  if (!previewVisible.value) return

  switch (e.key) {
    case 'Escape':
      handleClosePreview()
      break
    case 'ArrowLeft':
      handlePrev()
      break
    case 'ArrowRight':
      handleNext()
      break
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style lang="scss" module="styles">
.imagePreviewList {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.imagePreviewItemWrapper {
  position: relative;
}

.imagePreviewItem {
  display: block;
  cursor: pointer;
  user-select: none;
}

.imagePreviewDelete {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  transition: transform 0.2s;
  z-index: 10;

  &:hover {
    transform: scale(1.1);
  }
}

.imagePreviewDeleteIcon {
  width: 15px;
  height: 15px;
}
:global(.popup-center) {
  &.popContent {
    overflow: visible;
  }
}

.previewWrapper {
  :deep(.popup-mask) {
    background-color: rgba(0, 0, 0, 0.9) !important;
  }
}

.previewContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  width: 85vw;
  height: 85vh;
  position: relative;
}

.previewClose {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  transition: background-color 0.2s;
  z-index: 100;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.previewImage {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
}

.previewNav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  transition: background-color 0.2s;
  z-index: 100;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.previewNavPrev {
  left: -40px;
}

.previewNavNext {
  right: -40px;
}
</style>
